import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  setAuth0AccessTokenGetter,
  setAuth0LogoutIfSessionActive,
} from '@/lib/auth0-token';
import { useAuthStore } from '@/stores/useAuthStore';
import { extractRolesFromAccessToken, extractRolesFromClaims } from '@/lib/auth0-roles';

export default function Auth0TokenBridge() {
  const {
    getAccessTokenSilently,
    getIdTokenClaims,
    isAuthenticated,
    isLoading,
    user,
    logout: auth0Logout,
  } = useAuth0();
  const setAuth0Session = useAuthStore((s) => s.setAuth0Session);
  const rolesClaimKey = import.meta.env.VITE_AUTH0_ROLES_CLAIM;
  // The cache-bypassing fetch we've already started for a user id, kept with its
  // promise so a roleless account can't loop. A fresh token can change `user` and
  // restart the effect mid-retry; the restarted run awaits this instead of
  // deciding the user has no role from the token it was about to replace.
  const freshRetryRef = useRef<{ userId: string; roles: Promise<string[]> } | null>(null);

  useEffect(() => {
    setAuth0AccessTokenGetter(async () => {
      return await getAccessTokenSilently();
    });
    return () => {
      setAuth0AccessTokenGetter(null);
    };
  }, [getAccessTokenSilently]);

  useEffect(() => {
    setAuth0LogoutIfSessionActive(() => {
      if (!isAuthenticated) return;
      auth0Logout({ logoutParams: { returnTo: window.location.origin } });
    });
    return () => setAuth0LogoutIfSessionActive(null);
  }, [auth0Logout, isAuthenticated]);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      setAuth0Session({
        userId: '',
        roles: [],
        radiologistId: null,
      });
      return;
    }

    let cancelled = false;

    const resolveRoles = async (fresh: boolean) => {
      // Fetch the access token first so a fresh fetch also refreshes the cached ID token.
      const accessToken = await getAccessTokenSilently(
        fresh ? { cacheMode: 'off' } : undefined,
      ).catch(() => null);
      const idTokenClaims = await getIdTokenClaims().catch(() => undefined);

      return Array.from(
        new Set([
          ...(fresh
            ? []
            : extractRolesFromClaims(user as Record<string, unknown> | undefined, rolesClaimKey)),
          ...extractRolesFromClaims(
            idTokenClaims as Record<string, unknown> | undefined,
            rolesClaimKey,
          ),
          ...extractRolesFromAccessToken(accessToken, rolesClaimKey),
        ]),
      );
    };

    (async () => {
      let roles = await resolveRoles(false);
      // Cached tokens can predate the role assignment; bypass the cache once so
      // Auth0 re-runs the Login flow before we decide the user has no role.
      const userId = user?.sub ?? '';
      if (roles.length === 0 && !cancelled) {
        const pending = freshRetryRef.current;
        if (pending?.userId === userId) {
          roles = await pending.roles;
        } else {
          const retry = resolveRoles(true);
          freshRetryRef.current = { userId, roles: retry };
          roles = await retry;
        }
      }

      if (!cancelled) {
        if (import.meta.env.DEV) {
          console.info('[auth] resolved roles', roles);
        }
        setAuth0Session({
          userId: user?.sub ?? '',
          roles,
          radiologistId: null,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    getAccessTokenSilently,
    getIdTokenClaims,
    isAuthenticated,
    isLoading,
    rolesClaimKey,
    setAuth0Session,
    user,
  ]);

  return null;
}
